import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import { Users, Gift, Copy, Check, Share2, Loader2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface ReferralStats {
    referral_code: string | null;
    referral_url: string | null;
    total_referred: number;
    total_bonus_earned: number;
    referrals: Array<{
        referee_email: string;
        bonus_amount: number;
        created_at: string;
        status: string;
    }>;
}

const ReferralSection: React.FC = () => {
    const [stats, setStats] = useState<ReferralStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [copiedLink, setCopiedLink] = useState(false);
    const [copiedCode, setCopiedCode] = useState(false);

    useEffect(() => {
        fetchReferralStats();
    }, []);

    const fetchReferralStats = async () => {
        try {
            const token = localStorage.getItem('progeodata_token');
            if (!token) return;

            const res = await fetch(`${API_URL}/referrals/stats`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (res.ok) {
                const data = await res.json() as { success: boolean } & ReferralStats;
                if (data.success) {
                    setStats(data);
                }
            }
        } catch (err) {
            console.error('Failed to fetch referral stats:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = () => {
        if (stats?.referral_url) {
            navigator.clipboard.writeText(stats.referral_url);
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        }
    };

    const handleCopyCode = () => {
        if (stats?.referral_code) {
            navigator.clipboard.writeText(stats.referral_code);
            setCopiedCode(true);
            setTimeout(() => setCopiedCode(false), 2000);
        }
    };

    const handleShare = () => {
        if (stats?.referral_url && navigator.share) {
            navigator.share({
                title: 'Join ProGeoData - Get 50 Free Lookups',
                text: 'I\'m using ProGeoData for professional license data. Sign up with my link and we both get 50 free lookups!',
                url: stats.referral_url,
            }).catch(() => {
                handleCopyLink();
            });
        } else {
            handleCopyLink();
        }
    };

    if (loading) {
        return (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
                </div>
            </div>
        );
    }

    if (!stats) return null;

    return (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            {/* Header */}
            <div className="flex items-center gap-2 mb-6">
                <Users className="w-5 h-5 text-emerald-500" />
                <h2 className="text-lg font-bold text-white">Referral Program</h2>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-900 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-white">{stats.total_referred}</div>
                    <div className="text-xs text-slate-400">People Referred</div>
                </div>
                <div className="bg-slate-900 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-400">{stats.total_bonus_earned}</div>
                    <div className="text-xs text-slate-400">Bonus Lookups Earned</div>
                </div>
                <div className="bg-slate-900 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-sky-400">50</div>
                    <div className="text-xs text-slate-400">Per Referral</div>
                </div>
            </div>

            {/* Share Section */}
            <div className="bg-gradient-to-r from-emerald-500/10 to-sky-500/10 border border-emerald-500/20 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <Gift className="w-5 h-5 text-emerald-400" />
                    <span className="text-white font-bold text-sm">Share and earn 50 lookups</span>
                </div>

                {/* Referral Link */}
                {stats.referral_url && (
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sky-400 font-mono text-xs overflow-x-auto">
                            {stats.referral_url}
                        </div>
                        <button
                            onClick={handleCopyLink}
                            className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                            title="Copy link"
                        >
                            {copiedLink ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-slate-300" />}
                        </button>
                    </div>
                )}

                {/* Referral Code */}
                {stats.referral_code && (
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-slate-400 text-xs">Code:</span>
                        <code className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-emerald-400 font-mono text-sm">
                            {stats.referral_code}
                        </code>
                        <button
                            onClick={handleCopyCode}
                            className="p-1 hover:bg-slate-700 rounded transition-colors"
                            title="Copy code"
                        >
                            {copiedCode ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-slate-400" />}
                        </button>
                    </div>
                )}

                <Button variant="outline" size="sm" className="w-full" onClick={handleShare}>
                    <Share2 className="w-4 h-4 mr-2" /> Share Referral Link
                </Button>
            </div>

            {/* Recent Referrals */}
            {stats.referrals.length > 0 && (
                <div>
                    <h3 className="text-sm font-bold text-slate-300 mb-3">Recent Referrals</h3>
                    <div className="space-y-2">
                        {stats.referrals.map((referral, idx) => (
                            <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                                        <Users className="w-4 h-4 text-slate-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm text-slate-300">{referral.referee_email}</div>
                                        <div className="text-xs text-slate-500">
                                            {new Date(referral.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                                <span className="text-sm font-bold text-emerald-400">
                                    +{referral.bonus_amount}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {stats.referrals.length === 0 && (
                <div className="text-center py-4 text-slate-500 text-sm">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No referrals yet. Share your link to start earning!</p>
                </div>
            )}
        </div>
    );
};

export default ReferralSection;
